import { createContext, memo, ReactElement, Suspense } from 'react';
import { lazy } from 'utils/generic/lazy';
import { MenuItem } from 'interfaces/generic';
import { IMenuGroupWithSports } from 'interfaces/sportsbook-data-levels';
import { MenuFillSkeleton } from './layouts/fill/skeleton';
import { MenuUnderlineSkeleton } from './layouts/underline/skeleton';

export type TMenuProps = {
  layout: 'underline' | 'fill';
  items: MenuItem[] | null;
  sportsMenuItems?: (JSX.Element | JSX.Element[])[];
  groupItems?: IMenuGroupWithSports[] | null;
  selectedItem: MenuItem | null | undefined;
  onSelectItem: (arg0: MenuItem | IMenuGroupWithSports) => void;
  toggleGroupState?: (group: IMenuGroupWithSports) => void;
  FavMenuItem?: ReactElement | null;
  scrollAllToLeft?: boolean;
  orderIsLoading?: boolean;
  applyFavorite?: boolean;
};

const { MenuUnderline } = lazy(() => import('./layouts/underline'));
const { MenuFill } = lazy(() => import('./layouts/fill'));

const LAYOUTS = {
  underline: {
    view: MenuUnderline,
    skeleton: MenuUnderlineSkeleton
  },
  fill: {
    view: MenuFill,
    skeleton: MenuFillSkeleton
  }
};

export const MenuContext = createContext<Omit<TMenuProps, 'layout'>>({
  items: [],
  groupItems: [],
  selectedItem: null,
  sportsMenuItems: [],
  onSelectItem: () => {},
  toggleGroupState: () => {}
});

export const Menu = memo(
  ({
    FavMenuItem,
    layout,
    items,
    groupItems = null,
    selectedItem,
    onSelectItem,
    orderIsLoading,
    toggleGroupState,
    sportsMenuItems
  }: TMenuProps): ReactElement => {
    const Layout = LAYOUTS[layout];

    return (
      <MenuContext.Provider
        value={{
          items,
          groupItems,
          FavMenuItem,
          selectedItem,
          onSelectItem,
          toggleGroupState,
          sportsMenuItems: sportsMenuItems || []
        }}
      >
        {(items?.length || groupItems?.length) && !orderIsLoading ? (
          <Suspense fallback={<Layout.skeleton />}>
            <Layout.view />
          </Suspense>
        ) : (
          <Layout.skeleton />
        )}
      </MenuContext.Provider>
    );
  }
);
